import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FilesApi,
  type Lead,
  type LeadAttachment,
  LeadAttachmentKind,
  LeadsApi,
} from '@up2stay/up2promo-api-client';
import { Button, cn, ConfirmDialog, ImageZoom } from '@up2stay/up2promo-ui';
import {
  Download,
  FileText,
  GripVertical,
  ImagePlus,
  Loader2,
  Trash2,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { LEAD_DETAIL_SECTION_TITLE_CLASS } from '@/core/components/leads/lead-detail/lead-detail-section-card';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  triggerErrorHaptic,
  triggerSuccessHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const ALLOWED_DOCUMENT_EXTENSIONS = new Set(
  'pdf,doc,docx,xls,xlsx,txt,odt,ods,csv,jpg,jpeg,png,gif,webp'.split(','),
);

type AttachmentLabels = {
  title: string;
  empty: string;
  add: string;
  uploading: string;
  businessCard: string;
  image: string;
  document: string;
  uploadFailed: string;
  removeFailed: string;
  removed: string;
  added: string;
  fileTooLarge: string;
  fileTypeNotAllowed: string;
  open: string;
  remove: string;
  removeConfirmTitle: string;
  removeConfirmDescriptionPrefix: string;
  removeConfirmDescriptionSuffix: string;
  removeConfirmDescriptionFallback: string;
  confirmRemove: string;
  cancel: string;
  reorder: string;
  reorderFailed: string;
};

function reorderItems<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...items];
  const removed = result.splice(fromIndex, 1)[0];
  if (removed === undefined) return items;
  result.splice(toIndex, 0, removed);
  return result;
}

function isImageAttachment(attachment: LeadAttachment): boolean {
  return (
    attachment.kind === LeadAttachmentKind.IMAGE ||
    attachment.kind === LeadAttachmentKind.BUSINESS_CARD ||
    attachment.mimeType?.startsWith('image/') === true
  );
}

function attachmentTitle(attachment: LeadAttachment, labels: AttachmentLabels) {
  if (attachment.title?.trim()) return attachment.title.trim();
  if (attachment.kind === LeadAttachmentKind.BUSINESS_CARD) {
    return labels.businessCard;
  }
  if (attachment.kind === LeadAttachmentKind.IMAGE) return labels.image;
  return labels.document;
}

function attachmentSortId(attachment: LeadAttachment): string {
  const id = attachment.id?.trim();
  if (id) return id;
  const fileId = attachment.file?.id?.trim();
  return fileId ? `file-${fileId}` : '';
}

function AttachmentPreview({
  attachment,
  title,
}: {
  attachment: LeadAttachment;
  title: string;
}) {
  const url = attachment.file?.path;
  const isImage = isImageAttachment(attachment);

  if (isImage) {
    if (!url) {
      return (
        <div className="flex size-12 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
          <FileText className="h-5 w-5" />
        </div>
      );
    }

    return (
      <ImageZoom className="size-12 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
        <img src={url} alt={title} className="size-12 object-cover" />
      </ImageZoom>
    );
  }

  return (
    <div className="flex size-12 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
      <FileText className="h-5 w-5" />
    </div>
  );
}

function SortableAttachmentItem({
  attachment,
  labels,
  isRemoving,
  onRequestRemove,
}: {
  attachment: LeadAttachment;
  labels: AttachmentLabels;
  isRemoving: boolean;
  onRequestRemove: () => void;
}) {
  const isMobile = useIsMobile();
  const sortId = attachmentSortId(attachment);
  const title = attachmentTitle(attachment, labels);
  const url = attachment.file?.path;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex min-h-[4.5rem] items-center gap-3 rounded-md border border-border bg-card p-3 shadow-[var(--shadow-2xs)]',
        'transition-colors hover:bg-accent/40',
        isDragging && 'z-10 opacity-80 shadow-lg ring-2 ring-primary',
        isMobile && 'touch-pan-y',
      )}
      {...(isMobile ? { ...listeners, ...attributes } : {})}
    >
      <div
        data-dnd-grip
        {...listeners}
        {...attributes}
        className="shrink-0 touch-none cursor-grab rounded p-0.5 active:cursor-grabbing"
        aria-label={labels.reorder}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>

      <AttachmentPreview attachment={attachment} title={title} />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{title}</p>
      </div>

      {url && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          asChild
        >
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={labels.open}
          >
            <Download className="h-4 w-4" />
          </a>
        </Button>
      )}

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
        disabled={isRemoving || !attachment.id?.trim()}
        aria-label={labels.remove}
        onClick={onRequestRemove}
      >
        {isRemoving ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4" />
        )}
      </Button>
    </li>
  );
}

export function LeadDetailAttachments({
  lead,
  labels,
  onUpdated,
}: {
  lead: Lead;
  labels: AttachmentLabels;
  onUpdated: (lead: Lead) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [pendingRemoveAttachment, setPendingRemoveAttachment] =
    useState<LeadAttachment | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const lastOverIdRef = useRef<string | null>(null);
  const sensors = useDndProfileSensors();

  const attachments = useMemo(
    () =>
      [...(lead.attachments ?? [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      ),
    [lead.attachments],
  );

  const sortableIds = useMemo(
    () => attachments.map((attachment) => attachmentSortId(attachment)),
    [attachments],
  );

  const uploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;

    setIsUploading(true);
    try {
      let latestLead = lead;
      for (const file of list) {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast.error(labels.fileTooLarge.replace('{name}', file.name));
          continue;
        }
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        const isImage = file.type.startsWith('image/');
        if (!isImage && !ALLOWED_DOCUMENT_EXTENSIONS.has(ext)) {
          toast.error(labels.fileTypeNotAllowed.replace('{name}', file.name));
          continue;
        }

        const upload = await FilesApi.uploadFile({ body: { file } });
        const fileId = upload.data?.file?.id;
        if (!fileId) {
          throw new Error('upload missing file id');
        }

        const attached = await LeadsApi.addAttachment({
          path: {
            workspaceId: (latestLead.workspaceId ?? lead.workspaceId) as string,
            id: latestLead.id,
          },
          body: {
            fileId,
            kind: isImage
              ? LeadAttachmentKind.IMAGE
              : LeadAttachmentKind.DOCUMENT,
            mimeType: file.type || null,
            title: file.name.replace(/\.[^/.]+$/, '') || file.name,
          },
        });
        latestLead = attached.data;
      }

      onUpdated(latestLead);
      void triggerSuccessHaptic();
    } catch (error) {
      console.error('Lead attachment upload failed:', error);
      void triggerErrorHaptic();
      toast.error(labels.uploadFailed);
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async (attachment: LeadAttachment) => {
    const attachmentId = attachment.id?.trim();
    if (!attachmentId) {
      void triggerErrorHaptic();
      toast.error(labels.removeFailed);
      return;
    }

    setRemovingId(attachmentId);
    try {
      const removed = await LeadsApi.removeAttachment({
        path: {
          workspaceId: lead.workspaceId as string,
          id: lead.id,
          attachmentId,
        },
      });
      onUpdated(removed.data);
      void triggerSuccessHaptic();
    } catch (error) {
      console.error('Lead attachment remove failed:', error);
      void triggerErrorHaptic();
      toast.error(labels.removeFailed);
    } finally {
      setRemovingId(null);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (over == null) {
      lastOverIdRef.current = null;
      return;
    }
    const isNewSlot = lastOverIdRef.current !== over.id;
    const isBackToOwnSlot =
      over.id === active.id && lastOverIdRef.current === null;
    if (isNewSlot && !isBackToOwnSlot) {
      void triggerDragOverHaptic();
    }
    lastOverIdRef.current = over.id != null ? String(over.id) : null;
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    unlockBodyScrollAfterDrag();
    void triggerDragEndHaptic();

    const { active, over } = event;
    if (!over || active.id === over.id || isReordering) return;

    const fromIndex = attachments.findIndex(
      (item) => attachmentSortId(item) === active.id,
    );
    const toIndex = attachments.findIndex(
      (item) => attachmentSortId(item) === over.id,
    );
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = reorderItems(attachments, fromIndex, toIndex);
    const attachmentIds = reordered
      .map((item) => item.id?.trim())
      .filter((id): id is string => Boolean(id));

    if (attachmentIds.length !== reordered.length) {
      toast.error(labels.reorderFailed);
      return;
    }

    const optimisticLead: Lead = {
      ...lead,
      attachments: reordered.map((item, index) => ({
        ...item,
        order: index,
      })),
    };
    onUpdated(optimisticLead);

    try {
      setIsReordering(true);
      const reorderedLead = await LeadsApi.reorderAttachments({
        path: { workspaceId: lead.workspaceId as string, id: lead.id },
        body: { attachmentIds },
      });
      onUpdated(reorderedLead.data);
    } catch (error) {
      console.error('Lead attachment reorder failed:', error);
      onUpdated(lead);
      void triggerErrorHaptic();
      toast.error(labels.reorderFailed);
    } finally {
      setIsReordering(false);
    }
  };

  const pendingRemoveTitle = pendingRemoveAttachment
    ? attachmentTitle(pendingRemoveAttachment, labels)
    : '';

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className={LEAD_DETAIL_SECTION_TITLE_CLASS}>{labels.title}</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={isUploading || isReordering}
          onClick={() => inputRef.current?.click()}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ImagePlus className="h-4 w-4" />
          )}
          {isUploading ? labels.uploading : labels.add}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.odt,.ods,.csv"
          className="hidden"
          onChange={(e) => {
            const files = e.target.files;
            if (files?.length) void uploadFiles(files);
          }}
        />
      </div>

      {attachments.length === 0 ? (
        <p className="text-sm text-muted-foreground">{labels.empty}</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => {
            lockBodyScrollDuringDrag();
            void triggerDragStartHaptic();
          }}
          onDragOver={handleDragOver}
          onDragEnd={(event) => void handleDragEnd(event)}
        >
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-2">
              {attachments.map((attachment) => (
                <SortableAttachmentItem
                  key={attachmentSortId(attachment)}
                  attachment={attachment}
                  labels={labels}
                  isRemoving={removingId === attachment.id?.trim()}
                  onRequestRemove={() => setPendingRemoveAttachment(attachment)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <ConfirmDialog
        open={pendingRemoveAttachment !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoveAttachment(null);
        }}
        title={labels.removeConfirmTitle}
        description={
          pendingRemoveTitle ? (
            <>
              {labels.removeConfirmDescriptionPrefix}
              <span className="font-medium">{pendingRemoveTitle}</span>
              {labels.removeConfirmDescriptionSuffix}
            </>
          ) : (
            labels.removeConfirmDescriptionFallback
          )
        }
        confirmLabel={labels.confirmRemove}
        cancelLabel={labels.cancel}
        destructive
        onConfirm={() => {
          if (!pendingRemoveAttachment) return;
          const attachment = pendingRemoveAttachment;
          setPendingRemoveAttachment(null);
          void handleRemove(attachment);
        }}
      />
    </section>
  );
}
